import { apiClient } from '@/lib/api-client';
import type { ApiResponse, Building, CreateBuildingRequest, PaginatedResponse, UpdateBuildingRequest } from '@/types/api';

export interface GetBuildingsParams {
  propertyId: string;
  search?: string;
  page?: number;
  perPage?: number;
}

export async function getBuildings(params: GetBuildingsParams): Promise<PaginatedResponse<Building>> {
  const query = new URLSearchParams();
  query.set('propertyId', params.propertyId);
  if (params.search) query.set('search', params.search);
  if (params.page) query.set('page', String(params.page));
  if (params.perPage) query.set('perPage', String(params.perPage));

  const response = await apiClient.get<PaginatedResponse<Building>>(`/buildings?${query.toString()}`);
  return response.data;
}

export async function getBuilding(id: string): Promise<ApiResponse<Building>> {
  const response = await apiClient.get<ApiResponse<Building>>(`/buildings/${id}`);
  return response.data;
}

export async function createBuilding(data: CreateBuildingRequest): Promise<ApiResponse<Building>> {
  const response = await apiClient.post<ApiResponse<Building>>('/buildings', data);
  return response.data;
}

export async function updateBuilding(id: string, data: UpdateBuildingRequest): Promise<ApiResponse<null>> {
  const response = await apiClient.put<ApiResponse<null>>(`/buildings/${id}`, data);
  return response.data;
}

export async function deleteBuilding(id: string): Promise<ApiResponse<null>> {
  const response = await apiClient.delete<ApiResponse<null>>(`/buildings/${id}`);
  return response.data;
}

// May 14 batch — atomic bulk-create endpoints shared by the inline
// Add Property Step 2 flow, the standalone "+ Add Units" modal, and
// (Phase 6) the CSV upload path.

export interface BulkUnitItem {
  unitNumber: string;
  // Optional. When provided, the bulk-create endpoint looks up the device
  // within the caller's org and auto-assigns it to the new unit.
  deviceSerial?: string;
  // May 15 batch CSV expansion. When deviceSerial is provided, the
  // backend validates these against the inventory device and uses them
  // to update the paired device's config in the same atomic submit.
  deviceType?: 'WaterMeter' | 'SALTTransmitter';
  multiplier?: string;       // "1:10" or "10" — backend parses
  meterSerial?: string;      // → Device.ExternalMeterSerial (SALT)
  imr?: number;              // → Device.MechanicalOffset (SALT)
}

export interface BulkBuildingItem {
  name: string;
  units: BulkUnitItem[];
}

export interface BulkCreateBuildingsRequest {
  propertyId: string;
  buildings: BulkBuildingItem[];
}

export interface BulkCreateUnitsRequest {
  units: BulkUnitItem[];
}

export interface BulkCreateRowError {
  index: number;
  field: string;
  message: string;
}

export interface BulkCreateResult {
  succeeded: boolean;
  errors: BulkCreateRowError[];
  createdBuildingIds: string[];
  createdUnitIds: string[];
  // Count of devices auto-assigned during the submit (when the CSV /
  // bulk form supplied device serials). Used by the UI to surface a
  // "X devices linked" confirmation.
  assignedDeviceCount: number;
}

// `errorBody` is the 422 envelope — the API returns the same shape as the
// success path (sans the ApiResponse wrapper) so the modal can render
// per-row errors against the preview.
export interface BulkCreateResponse {
  ok: boolean;
  data?: BulkCreateResult;
  errorBody?: BulkCreateResult;
}

async function postBulk(url: string, body: unknown): Promise<BulkCreateResponse> {
  try {
    const response = await apiClient.post<ApiResponse<BulkCreateResult>>(url, body);
    return { ok: true, data: response.data.data ?? undefined };
  } catch (err: unknown) {
    const e = err as { response?: { status?: number; data?: BulkCreateResult } };
    if (e?.response?.status === 422 && e.response.data) {
      return { ok: false, errorBody: e.response.data };
    }
    throw err;
  }
}

export async function bulkCreateBuildings(data: BulkCreateBuildingsRequest): Promise<BulkCreateResponse> {
  return postBulk('/buildings/bulk', data);
}

export async function bulkCreateUnitsInBuilding(
  buildingId: string,
  data: BulkCreateUnitsRequest,
): Promise<BulkCreateResponse> {
  return postBulk(`/buildings/${buildingId}/units/bulk`, data);
}
